import { makeAutoObservable } from 'mobx';
import { t } from '@/shared/i18n';

const contextStagesMap: Record<EtaContext, EtaStage[]> = {
  global: [
    { key: 'startingUp', label: t('loading.global.startingUp'), weight: 1 },
    { key: 'initializingAccount', label: t('loading.global.initializingAccount'), weight: 1.5 },
    { key: 'preparingMusicEngine', label: t('loading.global.preparingMusicEngine'), weight: 2 },
    { key: 'almostReady', label: t('loading.global.almostReady'), weight: 1 }
  ],
  refreshSamples: [
    { key: 'refreshSamples_start', label: t('loading.refreshSamples.refreshSamples_start'), weight: 1 },
    { key: 'refreshSamples_done', label: t('loading.refreshSamples.refreshSamples_done'), weight: 1 }
  ],
  loadXML: [
    { key: 'loadXML_start', label: t('loading.loadXML.loadXML_start'), weight: 2 },
    { key: 'loadXML_done', label: t('loading.loadXML.loadXML_done'), weight: 1 }
  ],
  uploadXML: [
    { key: 'uploadXML_start', label: t('loading.uploadXML.uploadXML_start'), weight: 1 },
    { key: 'uploadXML_done', label: t('loading.uploadXML.uploadXML_done'), weight: 1 }
  ],
  editXML: [
    { key: 'editXML_process', label: t('loading.editXML.editXML_process'), weight: 1 },
  ],
  deleteXML: [
    { key: 'deleteXML_process', label: t('loading.deleteXML.deleteXML_process'), weight: 1 },
  ],
  CSV: [
    { key: 'CSV_process', label: t('loading.CSV.CSV_process'), weight: 1 },
  ],
  exportCSV: [
    { key: 'exportCSV_processingUrls', label: t('loading.exportCSV.exportCSV_processingUrls'), weight: 8 },
  ],
  quickEvent: [
    { key: 'quickEvent', label: t('loading.quickEvent.quickEvent'), weight: 1 },
  ]
};

type EtaLoaderStageKeyGlobal = 
| 'startingUp'
| 'initializingAccount'
| 'preparingMusicEngine'
| 'almostReady'

type EtaLoaderStageKeyRefresh = 
| 'refreshSamples_start'
| 'refreshSamples_done'

type EtaLoaderStageKeyLoad = 
| 'loadXML_start'
| 'loadXML_done'

type EtaLoaderStageKeyUpload = 
| 'uploadXML_start'
| 'uploadXML_done'

type EtaLoaderStageKeyEdit = 
  'editXML_process'

type EtaLoaderStageKeyDelete = 
 'deleteXML_process'
type EtaLoaderStageKeyQuickEvent =
  'quickEvent'
type EtaLoaderStageKeyCSV =
  'CSV_process'

type EtaLoaderStageKeyExportCSV = 
| 'exportCSV_fetchingSitemap'
| 'exportCSV_processingUrls'
| 'exportCSV_generatingFile'

export type EtaLoaderStageKey = EtaLoaderStageKeyGlobal | EtaLoaderStageKeyRefresh | EtaLoaderStageKeyLoad | EtaLoaderStageKeyUpload | EtaLoaderStageKeyEdit | EtaLoaderStageKeyDelete | EtaLoaderStageKeyQuickEvent | EtaLoaderStageKeyCSV | EtaLoaderStageKeyExportCSV
export type EtaContext = 'refreshSamples' | 'loadXML' |  'uploadXML' | 'editXML' |  'global' | 'deleteXML' | 'quickEvent' | 'CSV' | 'exportCSV';

// Define the structure for a loading stage
export interface EtaStage {
  key: EtaLoaderStageKey;
  label: string;
  weight: number; // relative weight for progress calculation
  progress?: number; // 0-1 for sub-progress within the stage
}

class EtaLoader {
  stages: EtaStage[] = [];
  currentStageIndex: number = 0;
  context: EtaContext | null = null;
  animatedPercent: number = 0;
  animationTimer: any = null;
  animationTarget: number = 0;
  stageStartTime: number = 0; // Track when stage started
  minStageDuration: number = 600; // Minimum time per stage in ms
  stageTimeoutUnexpectedDuration: number = 10000; // Unusual time per stage in ms
  stageTimeoutErrorDuration: number = 18000; // Max time per stage in ms
  stageTimeout: any = null;
  stageErrorTimeout: any = null;
  globalContextFinished: boolean = false;
  timeoutCheckInterval: any = null; // for periodic timeout checks
  
  // Session tracking to prevent parallel executions from interfering
  activeSessionId: string | null = null;

  private _hasError: boolean = false; // Error state for error UI instead of loader
  
  get hasError() {
    return this._hasError;
  }
  
  set hasError(value: boolean) {
    this._hasError = value;
  }
  
  errorMessage: string = '';
  errorAction: (() => void) | null = null;

// on error it doesnt set the error text, can simulate it by turning off mongo and it gives sample 404 
// for performance need to make it stop at some point to not keep running forever
  constructor() {
    makeAutoObservable(this);
    this.animatedPercent = 0;
  }

  // Start checking timeouts periodically
  private startTimeoutChecking() {
    if (this.timeoutCheckInterval) {
      clearInterval(this.timeoutCheckInterval);
    }
    this.timeoutCheckInterval = setInterval(() => {
      this.checkStageTimeout();
    }, 1000); // Check every second
  }

  // Stop timeout checking
  private stopTimeoutChecking() {
    if (this.timeoutCheckInterval) {
      clearInterval(this.timeoutCheckInterval);
      this.timeoutCheckInterval = null;
    }
  }

  public checkStageTimeout() {
    if (!this.stageStartTime) {
      console.log('[ETA] stageStartTime not set yet');
      return;
    }

    const elapsed = Date.now() - this.stageStartTime;
    const current = this.stages[this.currentStageIndex];
    
    if (!current || current.progress === 1) return;

    console.log(`[ETA] Elapsed time: ${elapsed}ms`);

    // Stop checking after 25 seconds - nothing more we can do
    if (elapsed > 25000) {
      console.log('[ETA]⏹️ 25s exceeded, stopping timeout checks');
      this.stopTimeoutChecking();
      return;
    }

    if (elapsed > this.stageTimeoutErrorDuration) {
      current.label = t('loading.error.loading_error_retry');
      console.log('Error timeout fired, label set to:', current.label);
    } else if (elapsed > this.stageTimeoutUnexpectedDuration) {
      current.label = t('loading.error.loading_error_timeout');
      console.log('Unexpected timeout fired, label set to:', current.label);
    }
  }

  setContext(context: EtaContext, sessionId?: string) {
  // Validate session if provided
  if (sessionId && !this.isSessionActive(sessionId)) {
    console.log('[ETA]⛔ Session', sessionId, 'is no longer active. Ignoring setContext.');
    return;
  }
  // Enforce: first context must be 'global' and must finish before any other
  if (!this.globalContextFinished && context !== 'global') {
    console.log('[ETA]⛔ Cannot set context to', context, 'before global context is finished');
    return;
  }
  if (
    this.stages[this.stages.length - 1]?.progress !== 1 &&
    this.context !== null &&
    context !== this.context
  ) {
    console.log('[ETA]⚠️ Attempted to set context before finishing last stage');
    return;
  }
  console.log('[ETA]🔄 Setting context:', context, 'for session:', sessionId);
  this.context = context;
  this.stageStartTime = 0; // Reset stage start time for new context
  // Automatically set stages for this context
  const stages = contextStagesMap[context];
  if (stages) {
    this.setStages(stages, context);
    // Optionally, set first stage to 0 progress for instant spinner
    if (stages.length > 0) {
      this.setStage(stages[0].key, 0, undefined, sessionId);
    }
  }
  this.startTimeoutChecking();
}

  // Clears the current context (call after last stage of current context is finished)
  clearContext() {
  if(this.stages[this.stages.length - 1]?.progress !== 1) {
    console.log('[ETA]⚠️ Attempted to clear context before finishing last stage');
    return;
  } else {
    console.log('[ETA]🧹 Clearing context:', this.context);
    this.stopTimeoutChecking();
    this.context = null;
    this.stages = [];
    this.currentStageIndex = 0;
    this.animatedPercent = 0;
    this.stageStartTime = 0; // Reset stage start time
    // Don't clear error state here - it should persist until explicitly cleared
  }
}

  // Show error state in the loader
  showError(message: string, action?: () => void) {
    this.hasError = true;
    this.errorMessage = message;
    this.errorAction = action || null;
    this.stopTimeoutChecking();
  }

  // Clear error state
  clearError() {
    this.hasError = false;
    this.errorMessage = '';
    this.errorAction = null;
  }

  // Start a new loading session and return its ID
  // This cancels any previous session
  startSession(): string {
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.activeSessionId = sessionId;
    console.log('[ETA] 🆕 Started new session:', sessionId);
    return sessionId;
  }

  // Check if a session is still active
  isSessionActive(sessionId: string): boolean {
    return this.activeSessionId === sessionId;
  }

  // Placeholder: set stages for a context
  setStages(stages: EtaStage[], context: EtaContext) {
    if (
      this.context === 'global' &&
      this.stages?.[this.stages.length - 1]?.progress !== 1 &&
      context !== 'global'
    ) {
      console.log('[ETA]⛔ Blocked context switch: global context not finished');
      return;
    }
    this.stages = stages.map(s => ({ ...s, progress: 0 }));
    this.currentStageIndex = 0;
    this.context = context;
    this.animatedPercent = 0;
    this.stageStartTime = 0; // Reset stage start time for new stages
    console.log(`[ETA]✅ Context switched to '${context}'`);
  }

  // Smoothly animate percent to target using requestAnimationFrame
  animateToPercent(targetPercent: number, duration = 200) {
    if (this.animationTimer) cancelAnimationFrame(this.animationTimer);
    const start = this.animatedPercent;
    const end = targetPercent;
    const startTime = performance.now();
    this.animationTarget = end;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      this.animatedPercent = start + (end - start) * progress;
      if (progress < 1) {
        this.animationTimer = requestAnimationFrame(animate);
      } else {
        this.animatedPercent = end && end > 100 ? 100 : end;
        this.animationTimer = null;
      }
    };

    this.animationTimer = requestAnimationFrame(animate);
  }

  // Placeholder: set current stage by key
  setStage(key: string, progress = 0, label?: string, sessionId?: string) {
  // Validate session if provided
  if (sessionId && !this.isSessionActive(sessionId)) {
    console.log('[ETA]⛔ Session', sessionId, 'is no longer active. Ignoring setStage', key);
    return;
  }
  const idx = this.stages.findIndex(s => s.key === key);
  const isFirstStage = idx === 0;
  const prevStageComplete = isFirstStage || this.stages[idx - 1]?.progress === 1;

  if (
    idx !== -1 && progress >= 0 &&
    progress <= 1 &&
    progress >= (this.stages[idx].progress ?? 0) &&
    progress !== this.stages[idx].progress &&
    prevStageComplete
  ) {
    if (idx !== this.currentStageIndex) {
      this.stageStartTime = Date.now();
      console.log(`[ETA] Starting stage ${key} at time:`, this.stageStartTime);
    }

    this.currentStageIndex = idx;
    this.stages[idx].progress = progress;

    // Check timeout after updating progress
    // this.checkStageTimeout();

    if (label) {
      this.stages[idx].label = label;
    }
    
    this.animateToPercent(this.percent);
    
    if (idx === this.stages.length - 1 && progress === 1) {
      if (this.context === 'global') {
        this.globalContextFinished = true;
        console.log('[ETA]✅ Global context finished (marked immediately)');
      }
      setTimeout(() => {
        this.clearContext();
      }, 2000);
    }
  }
}

  // Computed: get current percent (weighted)
  get percent() {
    const totalWeight = this.stages.reduce((sum, s) => sum + s.weight, 0) || 1;
    let doneWeight = 0;
    for (let i = 0; i < this.currentStageIndex; i++) {
      doneWeight += this.stages[i].weight;
    }
    const current = this.stages[this.currentStageIndex];
    const currentProgress = (current?.progress || 0) * (current?.weight || 0);
    return Math.round(((doneWeight + currentProgress) / totalWeight) * 100);
  }

  // Use this for smooth progress in UI
  get animatedPercentValue() {
    return Math.round(this.animatedPercent);
  }

  // Computed: get current message
  get message() {
    return this.stages[this.currentStageIndex]?.label || '';
  }
}

// Create and attach singleton to window
const etaLoader = new EtaLoader();
if (typeof window !== 'undefined') {
  (window as any).etaLoader = etaLoader;
}

export default etaLoader;